import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
/**
 * PrintAgentSection — Print Agent download + connection status + update indicator
 *
 * Shows:
 * - Connection status badge with update awareness
 * - Download link to installer resolved server-side via printAgent.getRelease
 *   (Phase 33 Plan 07 — replaces the prior hardcoded Supabase Storage URL).
 * - Quick setup instructions
 * - Manual "Update Now" button when agent reports an available update
 *
 * Surface: Production Settings tab — falls under AppLayout (Inventory App
 * design language, not Production stations). DESIGN.md §15.1: skeleton for
 * initial loads; §15.2: empty-state with icon + title + subtitle + CTA;
 * §16: never colour-as-only-information — pair amber bg with AlertTriangle.
 */

import { usePrintAgent } from '@/features/print-station/hooks/usePrintAgent';
import { expectedAgentFlavour } from '@/features/print-station/hooks/agentFlavour';
import {
  defaultAgentPort,
  readManualOverridePort,
  writeManualOverridePort,
} from '@/features/print-station/hooks/useAgentPort';
import { trpc } from '@/lib/trpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  ArrowUpCircle,
  CheckCircle2,
  ChevronDown,
  Copy,
  Download,
  Loader2,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react';

/** A release older than this triggers the amber stale-release banner. */
const STALE_AFTER_MS = 180 * 24 * 60 * 60 * 1000;

/**
 * Wave D item (W): detect whether the connected agent is the v2.x Tauri
 * shell so we can replace the deprecated `POST /update` button with
 * tray-panel instructions.
 *
 * Heuristic: the Tauri shell bundles the v1.6.x Node sidecar; standalone
 * v1.x agents top out at v1.5.x (the v1.x cohort that ran independently of
 * Tauri never got the v1.6 minor bump). So `version >= 1.6.0` means
 * "Tauri-shell agent, `/update` returns HTTP 410 Gone, updates flow through
 * `tauri-plugin-updater` in the tray panel". v1.5.x and below keep the
 * existing button (legacy in-place updater still works).
 *
 * We parse the leading "X.Y" pair only; pre-release suffixes (`-alpha.1`)
 * and build metadata (`+build.5`) don't affect the cutover.
 */
function isV2Agent(version: string | null): boolean {
  if (!version) return false;
  const match = version.match(/^(\d+)\.(\d+)/);
  if (!match) return false;
  const major = Number.parseInt(match[1]!, 10);
  const minor = Number.parseInt(match[2]!, 10);
  if (Number.isNaN(major) || Number.isNaN(minor)) return false;
  // 1.6.0+ OR major >= 2 = v2 cohort.
  return major > 1 || (major === 1 && minor >= 6);
}

export function PrintAgentSection() {
  const { t, i18n } = useTranslation('settings');
  const {
    isConnected,
    isLoading,
    version,
    updateAvailable,
    latestVersion,
    port,
    portStatus,
    retryPortDiscovery,
    ignoredAgent,
    sharedDropFolders,
  } = usePrintAgent();
  const siteFlavour = expectedAgentFlavour(import.meta.env.VITE_BACKEND_URL);
  const [isUpdating, setIsUpdating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [overrideInput, setOverrideInput] = useState<string>(() => {
    const stored = readManualOverridePort();
    return stored !== null ? String(stored) : '';
  });
  const [overrideExpanded, setOverrideExpanded] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const overrideInputRef = useRef<HTMLInputElement | null>(null);

  // Backend-resolved release manifest (Plan 33-07 Task 1). Replaces the prior
  // hardcoded `${VITE_SUPABASE_URL}/.../ThreadHeadsPrintAgentSetup.exe` which
  // 404'd as raw JSON when no release was published.
  //
  // M4 (Phase 33 5-agent review wave 2): pass i18n.language so the backend
  // computes `releasedAtAge` in the user's locale ("3 days ago" → "3 天前"
  // for zh). Prior version received an English string the frontend
  // interpolated into Chinese sentences ("版本 1.4.0，发布于 March 2026。").
  const releaseQuery = trpc.printAgent.getRelease.useQuery({ locale: i18n.language }, {
    // Manifest only changes on a release push; one fetch per session is plenty.
    staleTime: 60_000,
    // We render an empty-state for NOT_FOUND ourselves — no retry needed.
    retry: (failureCount, err) => {
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === 'NOT_FOUND' || code === 'UNAUTHORIZED') return false;
      return failureCount < 2;
    },
  });

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleUpdateNow = async () => {
    if (port === null) return;
    setIsUpdating(true);
    try {
      await fetch(`http://127.0.0.1:${port}/update`, { method: 'POST' });
    } catch {
      // Agent may disconnect as it restarts — that's expected
    }
    // Agent will restart; the probe will re-detect it
    timeoutRef.current = setTimeout(() => setIsUpdating(false), 30_000);
  };

  const handleSaveOverride = () => {
    const trimmed = overrideInput.trim();
    if (trimmed === '') {
      writeManualOverridePort(null);
    } else {
      const parsed = Number.parseInt(trimmed, 10);
      if (!Number.isInteger(parsed) || parsed < 49152 || parsed > 65535) {
        setOverrideError(t('production.printAgent.customPortInvalid'));
        return;
      }
      writeManualOverridePort(parsed);
    }
    setOverrideError(null);
    retryPortDiscovery();
    // Restore focus to the input so keyboard users can iterate on the
    // value without re-tabbing if discovery still doesn't find an agent.
    overrideInputRef.current?.focus();
  };

  // Determine badge content
  const renderBadge = () => {
    if (isUpdating) {
      return (
        <Badge variant="secondary" className="gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('production.printAgent.restarting')}
        </Badge>
      );
    }
    if (portStatus === 'discovering') {
      // BLU-410: distinct from "checking" — discovery races 5 candidates in
      // parallel for ~50–200ms before /status is even probed. A flat "Not
      // Connected" during this window would falsely look like an outage.
      // Search glyph + amber tint differentiates from checking (Loader2 +
      // neutral) so the two transient states aren't visually identical.
      return (
        <Badge variant="secondary" className="gap-1.5 bg-amber-100 text-amber-800 hover:bg-amber-100">
          <Search className="h-3 w-3 motion-safe:animate-pulse" aria-hidden="true" />
          {t('production.printAgent.discovering')}
        </Badge>
      );
    }
    if (isLoading) {
      return (
        <Badge variant="secondary" className="gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('production.printAgent.checking')}
        </Badge>
      );
    }
    if (isConnected && updateAvailable && latestVersion) {
      return (
        <Badge variant="secondary" className="gap-1.5 bg-amber-100 text-amber-800 hover:bg-amber-100">
          <ArrowUpCircle className="h-3 w-3" />
          {t('production.printAgent.updateAvailable')}
          {version ? ` (v${version} → v${latestVersion})` : ` (v${latestVersion})`}
        </Badge>
      );
    }
    if (isConnected) {
      const isUpToDate = latestVersion && version === latestVersion;
      return (
        <Badge variant="default" className="gap-1.5 bg-green-600 text-white hover:bg-green-600">
          <CheckCircle2 className="h-3 w-3" />
          {t('production.printAgent.connected')}{version ? ` v${version}` : ''}
          {isUpToDate ? ` ${t('production.printAgent.upToDate')}` : ''}
        </Badge>
      );
    }
    return (
      <Badge variant="destructive" className="gap-1.5">
        <XCircle className="h-3 w-3" />
        {t('production.printAgent.notConnected')}
      </Badge>
    );
  };

  const release = releaseQuery.data;

  // Stale if the release is older than 180 days. We only flag staleness — we
  // don't hide the download (operators still need an installer to fall back to).
  const isStale = useMemo(() => {
    if (!release) return false;
    const releasedAt = new Date(release.releasedAt).getTime();
    if (Number.isNaN(releasedAt)) return false;
    return Date.now() - releasedAt > STALE_AFTER_MS;
  }, [release]);

  // For releases > 30 days, switch from "3 weeks ago" → "March 2025" so the
  // caption stays meaningful long after the release ships (DESIGN.md §15.6:
  // captions must remain truthful after data ages).
  //
  // M4 (Phase 33 5-agent review wave 2): the >30-day fallback used date-fns
  // `format(..., 'MMMM yyyy')` which is English-only. Use
  // Intl.DateTimeFormat with the active i18n locale so a zh user sees a
  // localized month name. date-fns is no longer needed for this path.
  const ageString = useMemo(() => {
    if (!release) return '';
    const releasedAt = new Date(release.releasedAt).getTime();
    if (Number.isNaN(releasedAt)) return release.releasedAtAge;
    const ageMs = Date.now() - releasedAt;
    if (ageMs > 30 * 24 * 60 * 60 * 1000) {
      try {
        return new Intl.DateTimeFormat(i18n.language, {
          month: 'long',
          year: 'numeric',
        }).format(new Date(releasedAt));
      } catch {
        // Invalid locale — fall back to backend-computed string.
        return release.releasedAtAge;
      }
    }
    return release.releasedAtAge;
  }, [release, i18n.language]);

  const handleCopyHash = (hash: string) => {
    void navigator.clipboard.writeText(hash).then(
      () => toast.success(t('production.printAgent.hashCopied')),
      () => {
        /* navigator.clipboard rejected — fall through silently */
      },
    );
  };

  const renderDownloadGroup = () => {
    // ── Loading (initial fetch) — skeleton matches the button + caption rhythm.
    if (releaseQuery.isLoading) {
      return (
        <div className="space-y-2">
          <Skeleton className="h-11 w-48 rounded-md" />
          <Skeleton className="h-4 w-56 rounded-md" />
        </div>
      );
    }

    // ── Error states. NOT_FOUND + UNAUTHORIZED render inline; other errors
    // still render an inline fallback (no toast on initial paint to avoid
    // surprising users mid-load), with a retry button.
    if (releaseQuery.isError) {
      const code = (releaseQuery.error as { data?: { code?: string } })?.data?.code;

      if (code === 'NOT_FOUND') {
        return (
          <div
            role="status"
            className="rounded-md border border-dashed border-muted-foreground/30 p-6 text-center"
          >
            <Download className="h-8 w-8 text-muted-foreground mx-auto" aria-hidden="true" />
            <p className="mt-3 text-sm font-medium">
              {t('production.printAgent.noInstallerTitle')}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('production.printAgent.noInstallerBody')}
            </p>
            <Button variant="outline" size="sm" className="mt-3" asChild>
              <a href={t('production.printAgent.contactSupportHref')}>
                {t('production.printAgent.contactSupportLabel')}
              </a>
            </Button>
          </div>
        );
      }

      if (code === 'UNAUTHORIZED') {
        return (
          <div
            role="status"
            className="rounded-md border border-dashed border-muted-foreground/30 p-4 text-sm text-muted-foreground"
          >
            {t('production.printAgent.signInRequired')}
          </div>
        );
      }

      // Generic network / server error.
      // L3 (Phase 33 5-agent review wave 2): `role="alert"` (assertive)
      // for transient backend failures so screen readers announce
      // immediately. NOT_FOUND + UNAUTHORIZED above stay `role="status"`
      // (polite) — those are empty/info states, not errors.
      return (
        <div className="space-y-2" role="alert">
          <p className="text-sm font-medium">{t('production.printAgent.loadFailedTitle')}</p>
          <p className="text-xs text-muted-foreground">
            {t('production.printAgent.loadFailedBody')}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => void releaseQuery.refetch()}
            disabled={releaseQuery.isFetching}
          >
            {releaseQuery.isFetching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {t('production.printAgent.retry')}
          </Button>
        </div>
      );
    }

    // ── Success
    if (!release) return null;

    const shortHash = release.installerSha256
      ? `${release.installerSha256.slice(0, 12)}…`
      : null;

    return (
      <div className="space-y-2">
        {isStale && (
          <div
            role="status"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs">{t('production.printAgent.staleReleaseWarning')}</p>
          </div>
        )}
        <Button asChild className="h-11 gap-2">
          <a href={release.installerUrl} download>
            <Download className="h-4 w-4" aria-hidden="true" />
            {t('production.printAgent.downloadInstaller')}
          </a>
        </Button>
        <p className="text-xs text-muted-foreground mt-1">
          {t('production.printAgent.versionCaption', {
            version: release.version,
            age: ageString,
          })}
        </p>
        {release.installerSha256 && shortHash && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              {t('production.printAgent.verifyIntegrity')}
            </summary>
            <div className="mt-2 flex items-center gap-2">
              <code className="font-mono tabular-nums text-xs text-muted-foreground">
                {shortHash}
              </code>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                aria-label={t('production.printAgent.copyHash')}
                onClick={() => handleCopyHash(release.installerSha256 ?? '')}
              >
                <Copy className="h-3 w-3" aria-hidden="true" />
              </Button>
            </div>
          </details>
        )}
      </div>
    );
  };

  return (
    <div
      className="space-y-6"
      aria-busy={releaseQuery.isLoading}
    >
      {/* Connection Status */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">{t('production.printAgent.agentStatus')}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('production.printAgent.agentStatusDesc')}
          </p>
        </div>
        <div role="status" aria-live="polite" aria-atomic="true">
          {renderBadge()}
        </div>
      </div>

      {/* Another flavour's agent on this PC (e.g. prod seen from staging):
          say it is there on purpose and not used, so nobody "fixes" it. */}
      {ignoredAgent?.flavour && siteFlavour && (
        <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          {t('production.printAgent.ignoredAgent', {
            agentFlavour: t(`production.printAgent.flavour.${ignoredAgent.flavour}`),
            siteFlavour: t(`production.printAgent.flavour.${siteFlavour}`),
          })}
        </p>
      )}

      {/* Soft warning: this agent drops into the same folder as the other
          flavour's agent. Often deliberate (one printer watch folder), so it
          never blocks anything. */}
      {isConnected && sharedDropFolders.length > 0 && siteFlavour && (
        <p
          role="status"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
        >
          {t('production.printAgent.sharedDropFolder', {
            otherFlavour: t(
              `production.printAgent.flavour.${siteFlavour === 'staging' ? 'prod' : 'staging'}`,
            ),
          })}
        </p>
      )}

      {/* Update action — v1.x cohort still gets the `Update Now` button
          because its sidecar's `/update` endpoint performs the in-place
          updater. v2.x agents (Tauri shell, v1.6.0+ bundled sidecar) own
          updates via `tauri-plugin-updater` in the tray panel; the
          sidecar's `/update` returns HTTP 410 Gone, so clicking the button
          would look successful but do nothing. Replace it with instructions
          that point the operator at the right surface (Wave D item W). */}
      {isConnected && updateAvailable && !isUpdating && (
        <div
          role="status"
          className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
        >
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">
              {t('production.printAgent.versionAvailable', { version: latestVersion })}
            </p>
            <p className="text-xs text-amber-700">
              {isV2Agent(version)
                ? t('production.printAgent.updateViaTrayPanel')
                : t('production.printAgent.autoUpdateNote')}
            </p>
          </div>
          {/* Only show the legacy `/update` button when the agent is
              POSITIVELY identified as v1.x. A `null` version (probe
              in-flight, or status response missing the field) is
              treated as unknown — we'd rather hide the button than
              risk firing it against a v2 agent whose `/update`
              returns HTTP 410. */}
          {version !== null && !isV2Agent(version) && (
            <Button
              size="sm"
              variant="outline"
              className="h-11 gap-2 shrink-0"
              onClick={handleUpdateNow}
              disabled={isUpdating}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              {t('production.printAgent.updateNow')}
            </Button>
          )}
        </div>
      )}

      {/* Download */}
      <div className="space-y-2">
        <p className="text-sm font-medium">{t('production.printAgent.latestInstaller')}</p>
        {renderDownloadGroup()}
      </div>

      {/* BLU-410: manual port override — auto-expanded when discovery fails,
          collapsed otherwise (operator probably never needs it). When discovery
          has failed the toggle becomes a static heading: hiding the recovery
          path while it's needed would be a pit of failure. */}
      {(portStatus === 'not_found' || overrideExpanded) && (
        <div
          className={cn(
            'space-y-2 rounded-md border p-3',
            portStatus === 'not_found'
              ? 'border-amber-200 bg-amber-50'
              : 'border-dashed border-muted-foreground/30',
          )}
        >
          {portStatus === 'not_found' ? (
            <div className="space-y-1">
              <h4 className="text-sm font-medium">
                {t('production.printAgent.customPortTitle')}
              </h4>
              <p className="text-xs text-amber-800">
                {t('production.printAgent.customPortPromptNotFound')}
              </p>
            </div>
          ) : (
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm font-medium hover:underline"
              onClick={() => setOverrideExpanded((v) => !v)}
              aria-expanded={overrideExpanded}
              aria-controls="print-agent-port-override"
            >
              {t('production.printAgent.customPortTitle')}
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform motion-reduce:transition-none',
                  overrideExpanded && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>
          )}
          <form
            id="print-agent-port-override"
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveOverride();
            }}
          >
            <p className="text-xs text-muted-foreground">
              {t('production.printAgent.customPortHelp')}
            </p>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label htmlFor="print-agent-custom-port">
                  {t('production.printAgent.customPortLabel')}
                </Label>
                <Input
                  ref={overrideInputRef}
                  id="print-agent-custom-port"
                  type="number"
                  inputMode="numeric"
                  min={49152}
                  max={65535}
                  placeholder={String(defaultAgentPort())}
                  value={overrideInput}
                  onChange={(e) => {
                    setOverrideInput(e.target.value);
                    if (overrideError) setOverrideError(null);
                  }}
                  className="h-11 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  aria-invalid={overrideError !== null}
                  aria-describedby={overrideError ? 'print-agent-custom-port-error' : undefined}
                />
              </div>
              <Button type="submit" className="h-11 gap-2">
                {t('production.printAgent.customPortSave')}
              </Button>
            </div>
            {overrideError && (
              <p id="print-agent-custom-port-error" className="text-sm text-destructive">
                {overrideError}
              </p>
            )}
          </form>
        </div>
      )}

      {/* Quick Setup */}
      <div className="space-y-2">
        <p className="text-sm font-medium">{t('production.printAgent.quickSetup')}</p>
        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
          <li>{t('production.printAgent.step1')}</li>
          <li>{t('production.printAgent.step2')}</li>
          <li>{t('production.printAgent.step3')}</li>
          <li>{t('production.printAgent.step4')}</li>
        </ol>
      </div>
    </div>
  );
}
